import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface NavigationItem {
  id: string;
  title_ar: string;
  title_en: string;
  slug: string;
  external_url?: string;
  target: '_self' | '_blank';
  icon_class?: string;
  access_level: 'public' | 'authenticated' | 'admin';
  sort_order: number;
  is_active: boolean;
  parent_id?: string;
  children?: NavigationItem[];
}

export interface NavigationMenu {
  id: string;
  name: string;
  title_ar?: string;
  title_en?: string;
  items: NavigationItem[];
}

// Static fallback navigation - cached to avoid recreating
const FALLBACK_NAVIGATION = {
  id: 'fallback',
  name: 'main-header',
  title_ar: 'القائمة الرئيسية',
  title_en: 'Main Menu',
  items: [
    { id: '1', title_ar: 'الرئيسية', title_en: 'Home', slug: '/', target: '_self' as const, access_level: 'public' as const, sort_order: 1, is_active: true },
    { id: '2', title_ar: 'عن المجموعة', title_en: 'About Group', slug: '/about', target: '_self' as const, access_level: 'public' as const, sort_order: 2, is_active: true },
    { id: '3', title_ar: 'القطاعات', title_en: 'Sectors', slug: '/sectors', target: '_self' as const, access_level: 'public' as const, sort_order: 3, is_active: true },
    { id: '4', title_ar: 'الشركات', title_en: 'Companies', slug: '/companies', target: '_self' as const, access_level: 'public' as const, sort_order: 4, is_active: true },
    { id: '5', title_ar: 'المشاريع', title_en: 'Projects', slug: '/projects', target: '_self' as const, access_level: 'public' as const, sort_order: 5, is_active: true },
    { id: '6', title_ar: 'الشركاء', title_en: 'Partners', slug: '/partners', target: '_self' as const, access_level: 'public' as const, sort_order: 6, is_active: true },
    { id: '7', title_ar: 'المعرض', title_en: 'Gallery', slug: '/media-gallery', target: '_self' as const, access_level: 'public' as const, sort_order: 7, is_active: true },
    { id: '8', title_ar: 'اتصل بنا', title_en: 'Contact Us', slug: '/contact', target: '_self' as const, access_level: 'public' as const, sort_order: 8, is_active: true }
  ]
};

export const useOptimizedNavigationMenu = (menuName: string = 'main-header') => {
  return useQuery({
    queryKey: ['navigation-menu', menuName],
    queryFn: async (): Promise<NavigationMenu> => {
      try {
        // Simple query - just get navigation items directly
        const { data: items, error } = await supabase
          .from('navigation_items')
          .select('id, title_ar, title_en, slug, external_url, target, icon_class, access_level, sort_order, is_active, parent_id')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });

        if (error) {
          console.warn('Navigation items error, using fallback:', error);
          return FALLBACK_NAVIGATION;
        }

        if (!items || items.length === 0) {
          console.warn('No navigation items found, using fallback');
          return FALLBACK_NAVIGATION;
        }

        // Simple transform without complex hierarchy
        const navigationItems: NavigationItem[] = items.map(item => ({
          ...item,
          slug: item.slug || '#',
          target: (item.target === '_blank' ? '_blank' : '_self') as '_self' | '_blank',
          access_level: (item.access_level === 'admin' || item.access_level === 'authenticated') ? item.access_level as 'admin' | 'authenticated' : 'public',
        }));

        return {
          id: 'db-menu',
          name: menuName,
          title_ar: 'القائمة الرئيسية',
          title_en: 'Main Menu',
          items: navigationItems
        };
        
      } catch (error) {
        console.warn('Navigation query error, using fallback:', error);
        return FALLBACK_NAVIGATION;
      }
    },
    staleTime: 15 * 60 * 1000, // 15 minutes - navigation doesn't change often
    gcTime: 30 * 60 * 1000, // 30 minutes cache
    retry: 1, // Only retry once for navigation
    retryDelay: 3000, // 3 second delay
    enabled: !!menuName,
    refetchOnWindowFocus: false, // Don't refetch navigation on window focus
    refetchOnMount: false, // Don't refetch if data exists
    networkMode: 'offlineFirst', // Use cache first for better performance
  });
};